import {
  ElementRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {useSelector} from 'react-redux'
import {DateTime} from 'luxon'
import ButtonCalendarAtom from '../@atoms/to_forms/button_calendar/button_calendar.atom'
import ButtonTimeslotAtom from '../@atoms/to_forms/button_timeslot/button_timeslot_atom'
import {
  BookingRequestPayload,
  BookingWindowResponseProps,
  AdditionalResourceSummary,
  BookVisitDatePayload,
  DateTimeSlotProps,
} from '../props_reusable/visit'
import useError from './use_error'
import {RootState} from '../store_redux'
import visitService from '../services/visit.service'
import utils from '../utils'

const useStandardDateChoice = () => {
  const rootState = useSelector((state: RootState) => state)
  const {labels, modal} = rootState
  const {bookedSlotDetail, isShipmentFailed} = modal.data.standardDateChoice
  const {visitDate} = bookedSlotDetail || {}
  const [startDate, setStartDate] = useState<DateTime | null>(null)
  const [selectedSlot, setSelectedSlot] = useState<DateTimeSlotProps | null>(
    null,
  )
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const [dateSlots, setDateSlots] = useState<DateTimeSlotProps[]>([])
  const {error, setError} = useError()
  const formattedStartDate = startDate?.toFormat('LL/dd/yyyy') || ''
  const bookingWindowSummaryRef = useRef<AdditionalResourceSummary>()

  const disablePrev = useMemo(() => {
    const today = DateTime.now()
    if (startDate) return today >= startDate
    if (dateSlots.length) {
      const startDateForSlot = DateTime.fromISO(dateSlots[0].date)
      return today >= startDateForSlot
    }
    return true
  }, [startDate, dateSlots])

  type CalenderButtonSlotHandle = ElementRef<typeof ButtonCalendarAtom>
  const buttonCalenderAtomRef = useRef<CalenderButtonSlotHandle>(null)

  type ButtonSlotHandle = ElementRef<typeof ButtonTimeslotAtom>
  const buttonTimeslotAtomRef = useRef<ButtonSlotHandle>(null)

  useEffect(
    () => {
      if (formattedStartDate) {
        if (selectedSlot) {
          buttonTimeslotAtomRef.current?.removeSelected()
          setSelectedSlot(null)
        }
      }
    },
    // eslint-disable-next-line
    [formattedStartDate],
  )

  useEffect(
    () => {
      if (bookedSlotDetail) {
        const slotDate = DateTime.fromISO(bookedSlotDetail.visitDate).toFormat(
          'LL/dd/yyyy',
        )
        const dateData = dateSlots.find(
          d =>
            DateTime.fromISO(d.date).toFormat('LL/dd/yyyy') === slotDate &&
            d.availableToBook,
        )
        if (dateData) {
          buttonCalenderAtomRef.current?.setCalender(slotDate)
          // Added settimeout to update the state after certain time
          setTimeout(
            () =>
              buttonTimeslotAtomRef.current?.setDefaultValue(bookedSlotDetail),
            100,
          )
        }
      }
    },
    // eslint-disable-next-line
    [dateSlots, buttonCalenderAtomRef, loading],
  )

  const setTimeSlots = useCallback(
    (data: BookingWindowResponseProps) => {
      if (!startDate) {
        setStartDate(
          DateTime.fromISO(
            data.deliveryDays[0]?.date ||
              data.bookingWindowSummaryData.earliestVisitDate,
          ),
        )
      }

      if (
        !bookingWindowSummaryRef.current ||
        !bookingWindowSummaryRef.current?.earliestResourceVisitDate
      ) {
        const earliestResourceVisitDate = data.deliveryDays.find(d => {
          return d.bookingWindows.some(t => t.availableToBook)
        })?.date
        bookingWindowSummaryRef.current = earliestResourceVisitDate
          ? {
              earliestResourceVisitDate,
              ...data.bookingWindowSummaryData,
            }
          : {
              ...data.bookingWindowSummaryData,
            }
      }
      const slots = utils.formatDateTimeSlots(data)
      setDateSlots(slots)
    },
    [startDate, bookingWindowSummaryRef],
  )

  const fetchSlots = useCallback(
    (currentWeek: DateTime | null) => {
      setLoading(true)
      const payload: BookingRequestPayload = {
        ReturnEco: false,
        NumDays: 7,
        VisitNum: modal.data.standardDateChoice.visitId || 0,
      }
      if (currentWeek) payload.StartDate = currentWeek.toFormat('LL/dd/yyyy')
      visitService
        .getBookingDates<BookingWindowResponseProps>(payload)
        .then(setTimeSlots)
        .catch((err: Error) => setError('getBookingWindows', err.message))
        .finally(() => setLoading(false))
    },
    // eslint-disable-next-line
    [setTimeSlots],
  )

  // step to previous week
  const weekPrevFn = useCallback(
    (week: DateTime) => {
      // FETCH ON MOVING BACKWARD
      const prevWeek = week.minus({weeks: 1})
      setStartDate(prevWeek)
      fetchSlots(prevWeek)
    },
    [fetchSlots],
  )
  // step to next week
  const weekNextFn = useCallback(
    (week: DateTime) => {
      // FETCH ON MOVING FORWARD
      const nextWeek = week.plus({weeks: 1})
      setStartDate(nextWeek)
      fetchSlots(nextWeek)
    },
    [fetchSlots],
  )

  useEffect(
    () => {
      let sDate = startDate
      if (
        !isShipmentFailed &&
        visitDate &&
        !utils.isVisitUndated(visitDate) &&
        DateTime.fromISO(visitDate).startOf('day') >
          DateTime.now().startOf('day')
      ) {
        setStartDate(DateTime.fromISO(visitDate))
        sDate = DateTime.fromISO(visitDate)
      }
      fetchSlots(sDate)
    },
    // eslint-disable-next-line
    [visitDate, isShipmentFailed],
  )

  const calenderDateList = useMemo(() => {
    const calenderList = []
    if (startDate) {
      // eslint-disable-next-line
      for (const num of [0, 1, 2, 3, 4, 5, 6]) {
        const date = startDate.plus({days: num})
        calenderList.push({
          ap_type: utils.getDateSlotType(
            date.toFormat('LL/dd/yyyy'),
            dateSlots,
          ),
          ap_day_short: date.weekdayShort,
          ap_day: date.day,
          ap_date: date.toFormat('LL/dd/yyyy'),
        })
      }
    }
    return calenderList
  }, [startDate, dateSlots])

  const handleCalenderChangeEvent = useCallback(
    (d: string) => {
      setSelectedDate(d)
      if (selectedSlot) {
        buttonTimeslotAtomRef.current?.removeSelected()
        setSelectedSlot(null)
      }
    },
    [selectedSlot],
  )

  useEffect(() => {
    if (selectedDate) {
      const matchedDateSlots = dateSlots.filter(
        s => DateTime.fromISO(s.date).toFormat('LL/dd/yyyy') === selectedDate,
      )
      if (
        matchedDateSlots.length === 1 &&
        matchedDateSlots[0].availableToBook
      ) {
        buttonTimeslotAtomRef.current?.selectByIndex(0)
      }
    }
  }, [selectedDate, dateSlots])

  const getBookVisitPayload = () => {
    const selectedDateTimeSlot = buttonTimeslotAtomRef.current?.getSlot()
    return {
      ...bookingWindowSummaryRef.current,
      visitNumber: modal.data.bestDate.visitId,
      visitDate: selectedDateTimeSlot?.date || '',
      timeSlot: selectedDateTimeSlot?.timeSlotId || 0,
      timeSlotGroup: selectedDateTimeSlot?.timeSlotGroupId || 0,
    } as BookVisitDatePayload
  }

  return {
    labels,
    selectedDate,
    loading,
    error,
    startDate,
    weekNextFn,
    weekPrevFn,
    buttonTimeslotAtomRef,
    buttonCalenderAtomRef,
    dateSlots,
    selectedSlot,
    setSelectedSlot,
    calenderDateList,
    handleCalenderChangeEvent,
    disablePrev,
    setError,
    getBookVisitPayload,
  }
}

export default useStandardDateChoice
